/**
 * Document starts, drawn: a strip of scanned pages and a cut wherever the choice
 * starts a new document. Each drawing shows exactly what its choice does in the
 * engine's boundary detector (DocumentBoundaryDetector): the page that carries the
 * barcode or break sheet becomes the first page of the next document, and a
 * choice that only cuts at barcodes or sheets keeps a new-looking page with the
 * one before it. The bars under the strip are the documents that come out.
 */
export type DocumentStartsMode =
  | 'automatic'
  | 'existing-barcode'
  | 'automatic-barcode'
  | 'capturepoint-separator'
  | 'file-per-document';

/** What a page looks like: a first page (a title), a later page, and what is printed on it. */
type Sheet = { first?: boolean; barcode?: boolean; separator?: boolean };
/** `files`: the first and last page of each source file, drawn as an outline around its pages. */
type Strip = {
  pages: readonly Sheet[];
  cuts: readonly number[];
  files?: readonly (readonly [number, number])[];
};

const STRIPS: Record<DocumentStartsMode, Strip> = {
  // Paige reads the pages and starts a document where one looks like it begins.
  automatic: {
    pages: [{ first: true }, {}, { first: true }, {}, {}, { first: true }],
    cuts: [2, 5],
  },
  // Only a page with a matching barcode starts a document; the new-looking page 4 stays with page 3.
  'existing-barcode': {
    pages: [
      { first: true, barcode: true },
      {},
      { first: true, barcode: true },
      { first: true },
      {},
      { first: true, barcode: true },
    ],
    cuts: [2, 5],
  },
  // Matching barcodes start documents, and Paige finds the other starts too.
  'automatic-barcode': {
    pages: [
      { first: true, barcode: true },
      {},
      { first: true, barcode: true },
      { first: true },
      {},
      { first: true, barcode: true },
    ],
    cuts: [2, 3, 5],
  },
  // Only a Paige break sheet starts a document; the sheet leads the next one.
  'capturepoint-separator': {
    pages: [{ first: true }, {}, { first: true }, { separator: true }, { first: true }, {}],
    cuts: [3],
  },
  // Two files: each starts a document at its first page; the new-looking page 3 stays inside its file.
  'file-per-document': {
    pages: [{ first: true }, {}, { first: true }, {}, { first: true }, {}],
    cuts: [4],
    files: [
      [0, 3],
      [4, 5],
    ],
  },
};

const PAGE_W = 36,
  PAGE_H = 48,
  STEP = 48,
  LEFT = 12,
  TOP = 20;
const pageX = (index: number) => LEFT + index * STEP;

function Page({ sheet, index }: { sheet: Sheet; index: number }) {
  const x = pageX(index),
    y = TOP;
  if (sheet.separator)
    return (
      <g>
        <rect className="dsa-page dsa-sheet" x={x} y={y} width={PAGE_W} height={PAGE_H} rx="3" />
        {/* The break sheet prints one small code nine times. */}
        {[0, 1, 2].flatMap((row) =>
          [0, 1, 2].map((col) => (
            <rect
              key={`${row}-${col}`}
              className="dsa-mark"
              x={x + 6 + col * 9}
              y={y + 10 + row * 11}
              width="6"
              height="6"
              rx="1"
            />
          )),
        )}
      </g>
    );
  return (
    <g>
      <rect className="dsa-page" x={x} y={y} width={PAGE_W} height={PAGE_H} rx="3" />
      {sheet.first && (
        <rect
          className="dsa-mark"
          x={x + 5}
          y={y + 6}
          width={sheet.barcode ? 13 : 17}
          height="4"
          rx="1"
        />
      )}
      {sheet.barcode && (
        <path
          className="dsa-code"
          d={`M${x + 23} ${y + 5}v8M${x + 25.5} ${y + 5}v8M${x + 27} ${y + 5}v8M${x + 29.5} ${y + 5}v8M${x + 31} ${y + 5}v8`}
        />
      )}
      <path
        className="dsa-line"
        d={[0, 1, 2, 3]
          .map((line) => `M${x + 5} ${y + 18 + line * 7}h${line === 3 ? 14 : 26}`)
          .join('')}
      />
    </g>
  );
}

/** One cut: a dashed line in the gap before the page that starts a document, with scissors over it. */
function Cut({ before }: { before: number }) {
  const x = pageX(before) - (STEP - PAGE_W) / 2;
  return (
    <g className="dsa-cut">
      <path className="dsa-cut-line" d={`M${x} ${TOP - 3}V${TOP + PAGE_H + 3}`} />
      <g transform={`translate(${x} 9)`}>
        <circle className="dsa-cut-ring" cx="-3" cy="-3.2" r="2.4" />
        <circle className="dsa-cut-ring" cx="3" cy="-3.2" r="2.4" />
        <path className="dsa-cut-blade" d="M-1.6 -1.3L3.6 5.2M1.6 -1.3L-3.6 5.2" />
      </g>
    </g>
  );
}

export function DocumentStartsArt({ mode }: { mode: DocumentStartsMode }) {
  const strip = STRIPS[mode];
  const starts = [0, ...strip.cuts],
    documents = starts.map((start, index) => [
      start,
      (starts[index + 1] ?? strip.pages.length) - 1,
    ]);
  return (
    <svg
      className="dsa"
      viewBox="0 2 300 84"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-documents={documents.length}
    >
      {strip.files?.map(([from, to]) => (
        <rect
          key={from}
          className="dsa-file"
          x={pageX(from) - 2.5}
          y={TOP - 4}
          width={pageX(to) - pageX(from) + PAGE_W + 5}
          height={PAGE_H + 8}
          rx="5"
        />
      ))}
      {strip.pages.map((sheet, index) => (
        <Page key={index} sheet={sheet} index={index} />
      ))}
      {strip.cuts.map((before) => (
        <Cut key={before} before={before} />
      ))}
      {documents.map(([from, to], index) => (
        <path
          key={from}
          className={`dsa-document${index % 2 ? ' is-other' : ''}`}
          d={`M${pageX(from) + 2} ${TOP + PAGE_H + 12}H${pageX(to) + PAGE_W - 2}`}
        />
      ))}
    </svg>
  );
}
